/**
 * Markers
 * 
 * @Author: Focci
 * @Date: 2023-07-10 13:45:41
 * @Last Modified by: Focci
 * @Last Modified time: 2023-07-10 13:45:41
 */

'use client'

import PropTypes from 'prop-types'
import useAbortController from '@hook/useAbortController'
import { houseSearchType } from '@lib/config'
import { useMapState } from '@comp/mapbox'
import { fetchGet } from '@lib/fetch/client'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { isArray, filter, omit, isFunction, pick } from 'lodash-es'
import { useMap, Marker } from 'react-map-gl'
import { MarkerDot } from './Marker'
import { uniqueKey, lngLatStr, getFetchParam, initialMarkers } from './lib'

export default function Markers({
  type = 'property',
  url = 'map', // 接口地址
  param = null, // 接口请求参数
  needGroup = false, // 是否需要对点进行聚合
  exclude = [], // 需要排除的点
  moveMapRefetchMarkers = true, // 移动地图重新搜索Marker
  // 
  // 
  /**
   * 移动地图重新搜索Marker
   * 不受 moveMapRefetchMarkers, enabledMoveResearch开关的控制
   * 
   *  - true: 会删除 suburbId, districtId... 等参数
   *  - noRect 不会删除上面参数
   */
  researchAlwaysOnMove = false,
  marker = MarkerDot,
  disabledMarker = null, // { longitude, latitude }
  dataFilter = (d) => d,
  onDataUpdate = (d) => d,
  onMarkerClick = () => {},
  markerStyle = {}
}) {
  const CustomMarker = marker
  const { 
    updateViewedMarker, popups, setPopups, setMapData, enabledMoveResearch, locker 
  } = useMapState()

  const { getSignal } = useAbortController()
  const { current: mapbox } = useMap()
  const [markers, setMarkers] = useState([])
  const [rect, setRect] = useState() // 用于地图移动的时候，更新marker请求参数

  // 是否开启移动地图重新搜索Marker
  const enable = useMemo(() => (
    researchAlwaysOnMove || (enabledMoveResearch && moveMapRefetchMarkers)
  ), [researchAlwaysOnMove, enabledMoveResearch, moveMapRefetchMarkers])

  /**
   * 更新marker请求结果
   */
  const setMapdataCallback = useCallback((d) => {
    const typeName = houseSearchType[type]
    setMapData((prev) => ({ 
      ...prev, 
      [typeName]: { ...prev[typeName], ...d }
    }))
  }, [setMapData, type])
  
  /**
   * 获取marker数据
   */
  const fetchMarkers = useCallback((p) => {
    const { signal } = getSignal()
    setMapdataCallback({ loading: true })
    
    fetchGet(url, p, { signal }).then((data) => {
      setMapdataCallback({
        loading: false,
        total: data?.headers?.['x-total-count'],
        displayCount: data?.headers?.['x-total-display'],
      })
  
      // 清除需要禁用的 marker
      const markerData = filter(isArray(data) ? data : [], (f) => {
        const { latitude, longitude } = disabledMarker || {}
        return String(f.lat) !== latitude && String(f.lng) !== longitude
      })
      
      const dataArray = initialMarkers(dataFilter(markerData), needGroup, type)
      
      onDataUpdate(dataArray, type)
      setMarkers(dataArray)
    }).catch(() => {
      setMapdataCallback({ loading: false })
    })
  }, [
    type, needGroup, setMapdataCallback, getSignal,
    url, dataFilter, onDataUpdate, disabledMarker
  ])

  /**
   * 通过 propertyId 来过滤marker，以达到按优先级显示的目的
   */
  const filteredMarkers = useMemo(() => {
    if(isArray(exclude)) {
      return filter(markers, (f) => exclude.indexOf(f[lngLatStr]) === -1)
    }

    if(isFunction(exclude)) {
      return exclude({ markers, lngLatStr })
    }

    return filter(markers, (f) => !!(f?.lng && f?.lat))
  }, [exclude, markers])
  
  /**
   * 点击marker
   */
  const handleMarkerClick = useCallback((item, e) => {
    e?.originalEvent?.stopPropagation?.()
    
    if(popups?.[0]?.data?.[uniqueKey] === item[uniqueKey]) {
      setPopups([])
    } else {
      // 保存查看状态
      updateViewedMarker(item[uniqueKey])
      setPopups([{
        type,
        data: { ...item }
      }])
      onMarkerClick(item)
    }
  }, [onMarkerClick, popups, setPopups, updateViewedMarker, type])

  /**
   * 组织请求参数
   */
  const fetchParam = useMemo(() => {
    // rect 没实际作用, 只是触发更新
    let tmp = getFetchParam(mapbox, type, param, needGroup, rect)

    // - noRect 移动地图重新搜索Marker，但不想显示当前可视区域的内容
    if(enable && researchAlwaysOnMove !== 'noRect') {
      tmp = omit(tmp, ['regionId', 'districtId', 'suburbId', 'streetId', 'schoolId'])
    }
    
    return omit(tmp, ['order'])
  }, [enable, rect, mapbox, needGroup, param, researchAlwaysOnMove, type])

  useEffect(() => {
    const timer = setTimeout(() => fetchMarkers(fetchParam), 600)
    return () => clearTimeout(timer)
  
  // 这里不能将 fetchMarkers 加进来，否然会触发【无限】更新
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchParam])

  useEffect(() => {
    function handleRect(e) {
      if(enable || locker.current.mapboxMoveProactive) {
        setRect(pick(e.viewState, ['latitude', 'longitude']))
      }
      locker.current.mapboxMoveProactive = false
    }

    mapbox.on('moveend', handleRect)
    return () => { mapbox.off('moveend', handleRect) }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapbox, enable])

  if(!(isArray(filteredMarkers) && filteredMarkers.length > 0)) {
    return null
  }

  return filteredMarkers.map((item) => (
    <Marker
      key={`${item[uniqueKey]}`}
      latitude={item.lat}
      longitude={item.lng}
      onClick={(e) => handleMarkerClick(item, e)}
      style={markerStyle}
    >
      <CustomMarker 
        type={type}
        item={item}
        count={item?.points?.length}
      />
    </Marker>
  ))
}

Markers.propTypes = {
  type: PropTypes.oneOf([
    'property',
    'estimate',
    'rental',
    'sold',
    'rural',
    'commercialForSale',
    'commercialForLease',
    'school',
    'stateHouse', // 政府公屋
    'facility', // 周边设施
  ]),
  url: PropTypes.string,
  param: PropTypes.object,
  needGroup: PropTypes.bool,
  exclude: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.func,
  ]),
  marker: PropTypes.func,
  onDataUpdate: PropTypes.func,
}
